import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { renderEvidenceSections } from "./admin/ClubAdminCasePreviewPage";
import { ClubApi, ClubSessionCaseAccess } from "../../services/clubApi";
import { slugifyText } from "../../utils/clubCasePath";
import { normalizeEvidenceImageRecords } from "../../utils/clubAssetRefs";

const toEmbedUrl = (raw = "") => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (value.includes("/embed/")) return value;

  const watchMatch = value.match(/[?&]v=([^&]+)/);
  if (watchMatch?.[1]) return `https://www.youtube.com/embed/${watchMatch[1]}`;

  const shortMatch = value.match(/youtu\.be\/([^?]+)/);
  if (shortMatch?.[1]) return `https://www.youtube.com/embed/${shortMatch[1]}`;

  return value;
};

const getEvidenceFiles = (caseData: ClubSessionCaseAccess | null) => {
  const rawPayload = caseData?.casePayload as any;
  return Array.isArray(rawPayload?.evidence_files) ? rawPayload.evidence_files : [];
};

const isHttpUrl = (value: string) => /^https?:\/\//i.test(String(value || "").trim());

const resolveDirectUrlFromImageRecord = (image: any) => {
  if (typeof image === "string") {
    const direct = String(image || "").trim();
    return isHttpUrl(direct) ? direct : "";
  }
  if (!image || typeof image !== "object" || Array.isArray(image)) return "";

  const candidates = [
    image.url,
    image.image_url,
    image.imageUrl,
    image.src,
    image.access?.url,
    image.signedUrl,
  ];

  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (isHttpUrl(value)) return value;
  }

  return "";
};

const resolveEvidenceImageRecords = (
  images: any[],
  accessMap: Record<string, string> = {}
) =>
  (Array.isArray(images) ? images : [])
    .map((rawImage) => {
      const [image] = normalizeEvidenceImageRecords([rawImage]);
      const storageKey = String(image?.storageKey || "").trim();
      const accessUrl = String(accessMap[storageKey] || "").trim();
      const payloadUrl = resolveDirectUrlFromImageRecord(rawImage);
      const resolvedUrl = isHttpUrl(storageKey) ? storageKey : accessUrl || payloadUrl;
      if (!storageKey && !resolvedUrl) return null;

      return {
        storageKey,
        description: String(image?.description || "").trim(),
        url: resolvedUrl,
      };
    })
    .filter(Boolean) as Array<{ storageKey: string; description: string; url: string }>;

const getEvidenceThumbnailUrl = (evidenceFile: any, imageAccessMap: Record<string, string>) => {
  const normalizedImages = normalizeEvidenceImageRecords(
    Array.isArray(evidenceFile?.images) ? evidenceFile.images : []
  );
  for (const image of normalizedImages) {
    const directUrl = String((image as any)?.url || "").trim();
    if (directUrl) return directUrl;
    const signedUrl = String(imageAccessMap[String(image?.storageKey || "").trim()] || "").trim();
    if (signedUrl) return signedUrl;
  }
  return "";
};

const buildEvidenceId = (file: any, index: number) => {
  const explicitId = String(file?.id || "").trim();
  if (explicitId) return explicitId;
  const baseLabel = String(file?.title || file?.type || `evidence-${index + 1}`).trim();
  const slug = slugifyText(baseLabel) || `evidence-${index + 1}`;
  return `${String(index + 1).padStart(2, "0")}-${slug}`;
};

const getAgencyCaseCycleNumber = (rawCaseIndex: any) => {
  const parsedCaseIndex = Number(rawCaseIndex);
  if (!Number.isFinite(parsedCaseIndex) || parsedCaseIndex < 1001) return 1;
  return Math.max(1, parsedCaseIndex - 1000);
};

const humanizeEvidenceType = (value: string) =>
  String(value || "evidence")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim() || "Evidence";

const getCaseDynamicCounts = (evidenceFiles: any[], suspects: any[]) => {
  const files = Array.isArray(evidenceFiles) ? evidenceFiles : [];
  const suspectList = Array.isArray(suspects) ? suspects : [];

  return {
    suspectCount: suspectList.length,
    evidenceCount: files.length,
    imageCount: files.reduce(
      (count, file) => count + normalizeEvidenceImageRecords(Array.isArray(file?.images) ? file.images : []).length,
      0
    ),
    sectionCount: files.reduce(
      (count, file) => count + (Array.isArray(file?.sections) ? file.sections.length : 0),
      0
    ),
  };
};

const renderEvidencePager = (options: {
  previousItem?: { title?: string; type?: string; href: string } | null;
  nextItem?: { title?: string; type?: string; href: string } | null;
}) => {
  const humanizeType = (value: string) =>
    String(value || "evidence")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim() || "Evidence";

  const pagerItems = [
    options.previousItem
      ? {
          key: `previous-${options.previousItem.href}`,
          href: options.previousItem.href,
          direction: "Previous File",
          typeLabel: humanizeType(String(options.previousItem.type || "")),
          title: String(options.previousItem.title || "Previous evidence").trim() || "Previous evidence",
          align: "items-start text-left",
        }
      : null,
    options.nextItem
      ? {
          key: `next-${options.nextItem.href}`,
          href: options.nextItem.href,
          direction: "Next File",
          typeLabel: humanizeType(String(options.nextItem.type || "")),
          title: String(options.nextItem.title || "Next evidence").trim() || "Next evidence",
          align: "items-end text-right",
        }
      : null,
  ].filter(Boolean) as Array<{
    key: string;
    href: string;
    direction: string;
    typeLabel: string;
    title: string;
    align: string;
  }>;

  if (!pagerItems.length) return null;

  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {pagerItems.map((item) => (
        <Link
          key={item.key}
          to={item.href}
          className={`flex min-h-[112px] flex-col justify-between rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] px-5 py-4 transition hover:border-[#7ed0e3]/35 hover:bg-[linear-gradient(180deg,rgba(42,81,109,0.22),rgba(255,255,255,0.03))] ${item.align}`}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#d0c296]">{item.direction}</p>
            <p className="mt-2 text-sm font-semibold uppercase tracking-[0.14em] text-[#7ed0e3]">
              {item.typeLabel}
            </p>
          </div>
          <p className="mt-3 text-lg font-semibold leading-tight text-white">{item.title}</p>
        </Link>
      ))}
    </div>
  );
};

const extractFirstHttpUrl = (value: string) => {
  const match = String(value || "").match(/https?:\/\/[^\s)]+/i);
  return match ? String(match[0] || "").trim() : "";
};

const resolveBriefingVideoUrl = (
  caseAccess: ClubSessionCaseAccess | null,
  payload: any,
  newsScript: string
) => {
  const candidates = [
    caseAccess?.briefingVideoUrl,
    payload?.briefingVideoUrl,
    payload?.briefing_video_url,
    payload?.final_video_url,
    payload?.final_video_link,
    payload?.video_url,
    payload?.videoUrl,
    extractFirstHttpUrl(String(payload?.briefing_body || payload?.welcome_email || "")),
    extractFirstHttpUrl(String(newsScript || "")),
  ];

  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (!value) continue;
    if (/^https?:\/\//i.test(value)) return value;
  }

  return "";
};

const getGuestStorageKey = (token: string) => `club_guest_session_${String(token || "").trim()}`;

const readGuestSessionCache = (token: string) => {
  try {
    const raw = window.sessionStorage.getItem(getGuestStorageKey(token));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeGuestSessionCache = (token: string, payload: Record<string, unknown>) => {
  try {
    window.sessionStorage.setItem(getGuestStorageKey(token), JSON.stringify(payload));
  } catch {
    // Storage can be blocked in some browser privacy modes.
  }
};

const clearGuestSessionCache = (token: string) => {
  try {
    window.sessionStorage.removeItem(getGuestStorageKey(token));
  } catch {
    // Storage can be blocked in some browser privacy modes.
  }
};

const ClubPlayPage = () => {
  const { token = "", evidenceId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [sessionMeta, setSessionMeta] = useState<any>(null);
  const [caseAccess, setCaseAccess] = useState<ClubSessionCaseAccess | null>(null);
  const [activeEvidenceIndex, setActiveEvidenceIndex] = useState(0);
  const [guestSolveStatus, setGuestSolveStatus] = useState<any>(null);
  const [guestWarrantBusy, setGuestWarrantBusy] = useState(false);
  const [guestWarrantFeedback, setGuestWarrantFeedback] = useState("");
  const [guestWarrantError, setGuestWarrantError] = useState("");
  const refreshedEvidenceJoinRef = useRef<string>("");

  useEffect(() => {
    const isJsdomRuntime =
      typeof navigator !== "undefined" && /jsdom/i.test(String(navigator.userAgent || ""));
    if (isJsdomRuntime) return;
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      // jsdom does not implement scrollTo; ignore in non-browser test runtimes.
    }
  }, [location.pathname, evidenceId]);

  useEffect(() => {
    const cached = readGuestSessionCache(token);
    if (!cached) return;
    if (cached?.sessionMeta) setSessionMeta(cached.sessionMeta);
    if (cached?.caseAccess) setCaseAccess(cached.caseAccess);
    if (cached?.displayName) setDisplayName(String(cached.displayName));
    if (cached?.solveState) setGuestSolveStatus(cached.solveState);
  }, [token]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await ClubApi.getGroupSessionPublicByToken(token);
        setSessionMeta(response?.session || null);
      } catch (err: any) {
        setError(err?.response?.data?.error || "This session link is unavailable.");
      } finally {
        setLoading(false);
      }
    };

    if (token) load();
  }, [token]);

  const handleJoin = async () => {
    const trimmedName = String(displayName || "").trim();
    if (!trimmedName) {
      setError("Enter your display name to join.");
      return;
    }

    try {
      setJoining(true);
      setError("");
      const fingerprint = `${navigator.platform || ""}|${navigator.language || ""}|${navigator.userAgent || ""}`;
      const response = await ClubApi.joinGroupSessionByToken(token, {
        displayName: trimmedName,
        clientFingerprint: fingerprint.slice(0, 180),
      });

      setSessionMeta(response?.session || null);
      setCaseAccess(response?.caseAccess || null);
      setDisplayName(trimmedName);
      setActiveEvidenceIndex(0);
      setGuestSolveStatus(null);
      writeGuestSessionCache(token, {
        sessionMeta: response?.session || null,
        caseAccess: response?.caseAccess || null,
        displayName: trimmedName,
        solveState: null,
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || "Unable to join this session.");
    } finally {
      setJoining(false);
    }
  };

  const handleJoinAsDifferentGuest = () => {
    clearGuestSessionCache(token);
    setCaseAccess(null);
    setDisplayName("");
    setError("");
    setActiveEvidenceIndex(0);
    setGuestSolveStatus(null);
    setGuestWarrantError("");
    setGuestWarrantFeedback("");
    setSessionMeta((previous: any) => {
      if (!previous || typeof previous !== "object") return previous;
      return {
        ...previous,
        joinedDisplayName: "",
      };
    });
    if (evidenceId) {
      navigate(`/play/${encodeURIComponent(token)}`, { replace: true });
    }
  };

  const payload = (caseAccess?.casePayload || {}) as any;
  const evidenceImageAccessMap = (caseAccess?.evidenceImageAccessMap || {}) as Record<string, string>;
  const suspectImageAccessMap = (caseAccess?.suspectImageAccessMap || {}) as Record<string, string>;
  const newsScript = String(payload?.news_script || "").trim();
  const rawBriefingVideoUrl = resolveBriefingVideoUrl(caseAccess, payload, newsScript);
  const briefingVideoUrl = toEmbedUrl(rawBriefingVideoUrl);
  const logline = String(payload?.logline || "").trim();
  const suspects = Array.isArray(payload?.suspects) ? payload.suspects : [];
  const evidenceFiles = useMemo(() => getEvidenceFiles(caseAccess), [caseAccess]);
  const evidenceWithIds = useMemo(
    () =>
      evidenceFiles.map((file: any, index: number) => ({
        file,
        index,
        evidenceId: buildEvidenceId(file, index),
      })),
    [evidenceFiles]
  );
  const activeEvidence = evidenceFiles[activeEvidenceIndex] || null;
  const activeJoinedName = String(sessionMeta?.joinedDisplayName || displayName || "Detective").trim() || "Detective";
  const firstName = activeJoinedName.split(" ")[0] || activeJoinedName;
  const lastName = activeJoinedName.split(" ").slice(1).join(" ").trim();
  const replaceCaseTokens = (source: string) =>
    String(source || "")
      .replace(/\{ColdCaseClubUser\}/g, activeJoinedName)
      .replace(/\{role\}/gi, "Detective")
      .replace(/\{first\}/gi, firstName)
      .replace(/\{last\}/gi, lastName);
  const rawBriefingBody = String(
    payload?.briefing_body ||
      payload?.welcome_email ||
      caseAccess?.briefingBody ||
      ""
  ).trim();
  const briefingBody = replaceCaseTokens(
    rawBriefingBody ||
      "Welcome {ColdCaseClubUser}. Review the briefing, inspect every artifact, and build your timeline before naming a suspect."
  );
  const expiresAtDate = new Date(sessionMeta?.expiresAt);
  const expiresAtLabel = Number.isNaN(expiresAtDate.getTime()) ? "soon" : expiresAtDate.toLocaleString();
  const caseSummary =
    String(caseAccess?.summary || "").trim() ||
    logline ||
    "A case summary has not been published yet.";
  const caseSequenceNumber = getAgencyCaseCycleNumber(caseAccess?.caseIndex);
  const caseBackgroundAsset =
    evidenceFiles.find(
      (file: any) => String(file?.type || "").trim().toLowerCase() === "case_background"
    ) || evidenceFiles[0];
  const caseBackgroundImageUrl = caseBackgroundAsset
    ? getEvidenceThumbnailUrl(caseBackgroundAsset, evidenceImageAccessMap)
    : "";
  const caseDynamicCounts = getCaseDynamicCounts(evidenceFiles, suspects);
  const heroChips = [
    caseDynamicCounts.evidenceCount > 0 ? "Evidence Index Available" : "Evidence Pending",
    `${caseDynamicCounts.suspectCount} Suspect Dossiers`,
    `${caseDynamicCounts.evidenceCount} Evidence Records`,
    `${caseDynamicCounts.imageCount} Image Assets`,
  ];

  useEffect(() => {
    if (!evidenceWithIds.length) {
      setActiveEvidenceIndex(0);
      return;
    }

    if (!evidenceId) {
      setActiveEvidenceIndex(0);
      return;
    }

    const selectedIndex = evidenceWithIds.findIndex((item) => item.evidenceId === evidenceId);
    setActiveEvidenceIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [evidenceId, evidenceWithIds]);

  const activeEvidenceId = evidenceWithIds[activeEvidenceIndex]?.evidenceId || "";

  useEffect(() => {
    const trimmedName = String(displayName || "").trim();
    const shouldRefresh = Boolean(
      token &&
      caseAccess &&
      trimmedName &&
      !joining &&
      !loading
    );
    if (!shouldRefresh) return;

    const routeKey = evidenceId ? `evidence:${evidenceId}` : "main";
    const refreshKey = `${token}:${routeKey}:${trimmedName}`;
    if (refreshedEvidenceJoinRef.current === refreshKey) return;
    refreshedEvidenceJoinRef.current = refreshKey;

    const refreshAccess = async () => {
      try {
        const fingerprint = `${navigator.platform || ""}|${navigator.language || ""}|${navigator.userAgent || ""}`;
        const response = await ClubApi.joinGroupSessionByToken(token, {
          displayName: trimmedName,
          clientFingerprint: fingerprint.slice(0, 180),
        });
        if (response?.caseAccess) {
          setSessionMeta(response?.session || null);
          setCaseAccess(response.caseAccess);
          writeGuestSessionCache(token, {
            sessionMeta: response?.session || null,
            caseAccess: response.caseAccess,
            displayName: trimmedName,
            solveState: guestSolveStatus,
          });
        }
      } catch {
        // Keep existing cached case payload if silent refresh fails.
      }
    };

    refreshAccess();
  }, [caseAccess, displayName, evidenceId, guestSolveStatus, joining, loading, token]);

  const handleSubmitGuestWarrant = async (suspectIndex: number) => {
    try {
      setGuestWarrantBusy(true);
      setGuestWarrantError("");
      setGuestWarrantFeedback("");
      const response = await ClubApi.validateGroupSessionWarrant(token, { suspectIndex });
      const nextSolveState = {
        hasSubmittedWarrant: true,
        selectedSuspectIndex: Number(response?.warrant?.selectedSuspectIndex ?? suspectIndex),
        selectedSuspectName: String(response?.warrant?.selectedSuspectName || "").trim(),
        isCorrect: Boolean(response?.warrant?.isCorrect),
        solutionUnlocked: Boolean(response?.solutionUnlocked),
      };
      setGuestSolveStatus(nextSolveState);
      setGuestWarrantFeedback(
        response?.warrant?.isCorrect
          ? "Correct warrant request. The official solution is now unlocked for this session."
          : "Warrant filed. The official solution is now unlocked for this session."
      );
      writeGuestSessionCache(token, {
        sessionMeta,
        caseAccess,
        displayName,
        solveState: nextSolveState,
      });
    } catch (err: any) {
      setGuestWarrantError(
        err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to validate warrant."
      );
    } finally {
      setGuestWarrantBusy(false);
    }
  };

  if (loading) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-8 text-white">
        <div className="rounded-2xl border border-cyan-300/20 bg-gradient-to-br from-[#0a1322] via-[#151a26] to-[#261416] p-6">
          <p className="text-sm text-cyan-100">Loading session...</p>
        </div>
      </section>
    );
  }
  if (!sessionMeta && error) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-8 text-white">
        <div className="rounded-2xl border border-rose-300/30 bg-rose-950/40 p-6">
          <p className="text-sm text-rose-200">{error}</p>
        </div>
      </section>
    );
  }

  if (!caseAccess) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-8 text-white">
        <div className="rounded-2xl border border-cyan-300/20 bg-gradient-to-br from-[#0a1322] via-[#151a26] to-[#261416] p-6">
          <p className="text-xs uppercase tracking-[0.25em] text-cyan-200">Group Session Invite</p>
          <h1 className="mt-2 text-3xl font-black">{sessionMeta?.caseName || "Cold Case Session"}</h1>
          <p className="mt-2 text-sm text-zinc-200">
            Hosted by {sessionMeta?.hostFirstName || "a member"}. Ends {expiresAtLabel}.
          </p>

          <label className="mt-5 block text-sm">
            <span className="mb-1 block font-semibold">Display name</span>
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Your name"
              className="w-full rounded-lg border border-white/20 bg-zinc-900 px-3 py-2"
            />
          </label>

          <button
            type="button"
            onClick={handleJoin}
            disabled={joining}
            className="mt-4 rounded-lg bg-cyan-300 px-4 py-2.5 text-sm font-semibold text-[#0a1222] disabled:opacity-60"
          >
            {joining ? "Joining..." : "Join Session"}
          </button>

          {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}

          <div className="mt-6 rounded-xl border border-amber-300/25 bg-amber-500/10 p-4">
            <p className="text-sm text-amber-50">
              Would you like to become a club member? Press here to receive your monthly cold case.
            </p>
            <Link
              to="/agency/dashboard"
              className="mt-3 inline-block rounded-md bg-amber-300 px-3 py-2 text-xs font-bold text-[#241603]"
            >
              Become An Agency Member
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (evidenceId && !activeEvidence) {
    return (
      <section className="mx-auto max-w-4xl px-4 py-6 text-white">
        <div className="rounded-2xl border border-white/15 bg-zinc-950/80 p-5">
          <p className="text-rose-300">Evidence file not found.</p>
          <div className="mt-4">
            <Link
              to={`/play/${encodeURIComponent(token)}`}
              className="rounded-md border border-white/20 px-3 py-2 text-xs font-semibold text-zinc-200 hover:bg-white/10"
            >
              Back to Case File
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (evidenceId && activeEvidence) {
    const selectedEvidenceImageAssets = normalizeEvidenceImageRecords(activeEvidence?.images || []).map((image) => ({
      storageKey: image.storageKey,
      description: image.description,
      url:
        String(evidenceImageAccessMap[String(image.storageKey || "").trim()] || "").trim() ||
        String((image as any)?.url || "").trim() ||
        "",
    }));
    const witnessEvidenceSections = evidenceWithIds
      .map((item) => item.file)
      .find((file) => String(file?.type || "").trim().toLowerCase() === "witness_statements")?.sections;
    const previousEvidence = activeEvidenceIndex > 0 ? evidenceWithIds[activeEvidenceIndex - 1] : null;
    const nextEvidence =
      activeEvidenceIndex < evidenceWithIds.length - 1 ? evidenceWithIds[activeEvidenceIndex + 1] : null;

    return (
      <section className="mx-auto max-w-[1180px] px-1.5 py-2 text-white sm:px-4 sm:py-5 md:p-7">
        <div className="relative overflow-hidden rounded-[22px] border border-amber-200/15 bg-[radial-gradient(circle_at_top_left,rgba(120,53,15,0.28),transparent_28%),linear-gradient(135deg,#08080b,#16110a,#0f172a)] p-2.5 shadow-[0_24px_70px_rgba(0,0,0,0.45)] sm:rounded-[32px] sm:p-6">
          <div className="flex flex-col items-start gap-2.5 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#d0c296] sm:text-xs sm:tracking-[0.28em]">
                Agency Evidence File
              </p>
              <h1 className="mt-1 text-[1.72rem] font-black leading-[1.02] text-white sm:mt-2 sm:text-3xl sm:leading-tight md:text-4xl">
                {String(activeEvidence?.title || activeEvidence?.type || "Evidence File")}
              </h1>
              <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#7ed0e3] sm:mt-2 sm:text-xs sm:tracking-[0.22em]">
                {humanizeEvidenceType(String(activeEvidence?.type || "evidence"))}
              </p>
            </div>
            <Link
              to={`/play/${encodeURIComponent(token)}`}
              className="rounded-full border border-cyan-300/35 bg-cyan-400/10 px-3 py-2 text-xs font-semibold text-cyan-100 hover:bg-cyan-400/15 sm:px-4 sm:text-sm"
            >
              Back to Case File
            </Link>
          </div>

          <div className="mt-3 grid gap-2 md:mt-6 md:grid-cols-4 md:gap-4">
            <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">Date</p>
              <p className="mt-1 text-[13px] text-white/85 sm:mt-1.5 sm:text-sm">{String(activeEvidence?.date || "No date")}</p>
            </div>
            <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">Source</p>
              <p className="mt-1 text-[13px] text-white/85 sm:mt-1.5 sm:text-sm">{String(activeEvidence?.source || "No source")}</p>
            </div>
            <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">Confidentiality</p>
              <p className="mt-1 text-[13px] text-white/85 sm:mt-1.5 sm:text-sm">
                {String(activeEvidence?.confidentiality || "internal")}
              </p>
            </div>
            <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">Evidence Type</p>
              <p className="mt-1 text-[13px] text-white/85 sm:mt-1.5 sm:text-sm">
                {String(activeEvidence?.type || "evidence").replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase())}
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-[18px] border border-amber-100/12 bg-[linear-gradient(180deg,rgba(255,248,235,0.06),rgba(255,255,255,0.02))] p-3 sm:mt-6 sm:rounded-3xl sm:p-5">
            {renderEvidenceSections(
              {
                ...activeEvidence,
                images: selectedEvidenceImageAssets,
              },
              {
                suspects,
                suspectImageAccessMap,
                caseId: String(caseAccess?.generatedCaseId || ""),
                caseTitle: String(payload?.title || payload?.case_title || caseAccess?.title || ""),
                finalView: true,
                witnessSections: Array.isArray(witnessEvidenceSections) ? witnessEvidenceSections : [],
                onOpenImage: (url: string) => {
                  if (!url) return;
                  window.open(url, "_blank", "noopener,noreferrer");
                },
                solveGate: {
                  enabled: true,
                  mode: "guest",
                  suspects,
                  suspectImageAccessMap,
                  actorLabel: activeJoinedName,
                  currentRankLabel: "Guest Detective",
                  solveStatus: guestSolveStatus,
                  onSubmitWarrant: handleSubmitGuestWarrant,
                  busy: guestWarrantBusy,
                  feedback: guestWarrantFeedback,
                  error: guestWarrantError,
                },
              }
            )}
          </div>
          {renderEvidencePager({
            previousItem: previousEvidence
              ? {
                  title: String(previousEvidence.file?.title || ""),
                  type: String(previousEvidence.file?.type || ""),
                  href: `/play/${encodeURIComponent(token)}/evidence/${previousEvidence.evidenceId}`,
                }
              : null,
            nextItem: nextEvidence
              ? {
                  title: String(nextEvidence.file?.title || ""),
                  type: String(nextEvidence.file?.type || ""),
                  href: `/play/${encodeURIComponent(token)}/evidence/${nextEvidence.evidenceId}`,
                }
              : null,
          })}
        </div>
      </section>
    );
  }

  return (
      <section className="mx-auto max-w-[1500px] px-3 py-4 text-white sm:px-4 sm:py-5 md:p-7">
        <div className="border-b border-white/8 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#7e6c44] text-sm tracking-[0.28em] text-[#dccca9]">
                C
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#9f9a8e]">Cold Case Agency</p>
                <p className="text-3xl font-semibold leading-none text-white">Session Case View</p>
              </div>
            </div>
            <Link
              to="/agency"
              className="rounded-full border border-white/12 bg-white/[0.03] px-5 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white/90 hover:bg-white/[0.08]"
            >
              Agency Home
            </Link>
          </div>
        </div>

        <div className="relative mt-10 overflow-hidden rounded-[32px] border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(187,140,64,0.10),transparent_24%),radial-gradient(circle_at_70%_30%,rgba(67,95,138,0.18),transparent_34%),linear-gradient(180deg,rgba(15,19,28,0.96),rgba(9,12,18,0.98))] p-7 shadow-[0_30px_90px_rgba(5,10,30,0.55)]">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:24px_24px] opacity-20" />
          <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#d1c39a]">
                {`Case File ${String(caseSequenceNumber).padStart(3, "0")} / Session Access`}
              </p>
              <h1 className="mt-5 text-5xl font-black leading-[0.92] text-[#f0ede6] sm:text-6xl">
                {caseAccess.title}
              </h1>
              <p className="mt-5 max-w-4xl whitespace-pre-wrap text-base leading-8 text-[#c8c4bb] sm:text-[1.05rem]">
                {caseSummary}
              </p>

	              <div className="mt-10 grid gap-4 md:grid-cols-3">
	                <article className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
	                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Status</p>
	                  <p className="mt-3 text-2xl font-semibold text-white">Session Access</p>
	                </article>
	                <article className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
	                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Assigned Cycle</p>
	                  <p className="mt-3 text-2xl font-semibold text-white">Cycle {caseSequenceNumber}</p>
	                </article>
	                <article className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
	                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Joined As</p>
	                  <p className="mt-3 text-2xl font-semibold text-white">{activeJoinedName}</p>
	                </article>
	              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {heroChips.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[11px] uppercase tracking-[0.24em] text-[#d2cdc1]"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

	            <div className="rounded-[28px] border border-white/10 bg-[radial-gradient(circle_at_30%_20%,rgba(90,116,153,0.18),transparent_16%),linear-gradient(180deg,rgba(15,28,46,0.95),rgba(9,16,29,0.9))] p-4">
	              <div className="relative overflow-hidden rounded-[22px] border border-white/10 bg-black/30">
	                {caseBackgroundImageUrl ? (
	                  <img
	                    src={caseBackgroundImageUrl}
	                    alt={`${caseAccess.title} case background`}
	                    className="aspect-[16/9] w-full object-cover saturate-[0.9] contrast-[1.05] brightness-[0.86]"
	                    loading="lazy"
	                  />
	                ) : (
	                  <div className="aspect-[16/9] bg-[radial-gradient(circle_at_30%_20%,rgba(103,132,170,0.18),transparent_12%),linear-gradient(180deg,rgba(12,20,32,0.98),rgba(10,15,24,0.88))]" />
	                )}
	                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(3,8,18,0.08),rgba(3,8,18,0.32))]" />
	              </div>
	              <div className="mt-4 grid gap-3 sm:grid-cols-2">
	                <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
	                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Session Host</p>
	                  <p className="mt-2 text-lg font-semibold text-white">
	                    {String(sessionMeta?.hostFirstName || "Agency Member")}
	                  </p>
	                </div>
	                <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
	                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Access Window</p>
	                  <p className="mt-2 text-lg font-semibold text-white">{expiresAtLabel}</p>
	                </div>
	              </div>
	              <div className="mt-3 rounded-2xl border border-white/8 bg-black/20 p-4">
	                <p className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Evidence Volume</p>
	                <p className="mt-2 text-lg font-semibold text-white">{caseDynamicCounts.sectionCount} sections</p>
	              </div>
	            </div>
          </div>
        </div>

      <div className="mt-7 grid gap-6 lg:grid-cols-[1.12fr_0.88fr]">
        <div className="space-y-6">
          <article className="rounded-[28px] border border-white/10 bg-[#090e16]/95 p-6">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">Broadcast Feed</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight text-white">News Story</h2>
              </div>
              {rawBriefingVideoUrl ? (
                <a
                  href={rawBriefingVideoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-cyan-300/25 bg-cyan-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100"
                >
                  Open Final Video
                </a>
              ) : null}
            </div>

            {briefingVideoUrl ? (
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-black">
                <iframe
                  src={briefingVideoUrl}
                  title="Briefing video"
                  className="aspect-video w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/20 bg-white/5 p-5 text-sm text-zinc-300">
                Add a briefing video URL in admin for this case.
              </div>
            )}

          </article>

          <article className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(9,13,20,0.96))] p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">Assignment Notes</p>
                <h3 className="mt-2 text-2xl font-bold text-white">Briefing Notes</h3>
              </div>
              <button
                type="button"
                onClick={handleJoinAsDifferentGuest}
                className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80 hover:bg-white/[0.08]"
              >
                Join as Different Guest
              </button>
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-7 text-[#d0d5df]">
              {briefingBody}
            </p>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#9f9a8e]">Setting</p>
                <p className="mt-2 text-sm font-semibold text-white">
                  {String(payload?.setting?.place || "Unknown place")}
                </p>
                <p className="mt-1 text-sm text-white/60">
                  {String(payload?.setting?.time || "Unknown time")}
                </p>
              </div>
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#9f9a8e]">Case Status</p>
                <p className="mt-2 text-sm font-semibold text-white">SESSION ACCESS</p>
                <p className="mt-1 text-sm text-white/60">
                  You are in {String(sessionMeta?.hostFirstName || "a member")}&apos;s session.
                </p>
              </div>
            </div>
          </article>
        </div>

        <aside className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(9,13,20,0.96))] p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">Field Archive</p>
              <h3 className="mt-2 text-2xl font-bold text-white">Evidence Index</h3>
            </div>
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-white/65">Case View</span>
          </div>

          <div className="space-y-3">
	            {evidenceFiles.map((file: any, index: number) => {
	              const isCaseBackground = String(file?.type || "").trim().toLowerCase() === "case_background";
	              const evidenceLink = `/play/${encodeURIComponent(token)}/evidence/${
	                evidenceWithIds[index]?.evidenceId || buildEvidenceId(file, index)
	              }`;
              const isVictimProfile =
                String(file?.type || "").trim().toLowerCase() === "victim_profile";

              return (
	                <div
	                  key={`${file?.id || file?.title || "evidence"}-${index}`}
	                  className="rounded-[24px] border border-white/8 bg-white/[0.03] p-3"
	                >
	                  <Link to={evidenceLink} className="block text-left transition hover:opacity-90">
	                    <p className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">
	                      {humanizeEvidenceType(String(file?.type || "evidence"))}
	                    </p>
                    <p className="mt-2 text-base font-semibold leading-tight text-white">
                      {String(file?.title || `Evidence ${index + 1}`)}
                    </p>
                    <span className="mt-2 inline-block text-[11px] uppercase tracking-[0.18em] text-[#7ed0e3]">
                      Open file
                    </span>
                  </Link>

                  {isVictimProfile && suspects.length > 0 ? (
                    <div className="mt-3 border-t border-white/10 pt-3">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-amber-100/65">
                        Included Suspect Dossiers
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {suspects.map((suspect: any, suspectIndex: number) => (
                          <span
                            key={`guest-suspect-chip-${suspectIndex}`}
                            className="rounded-full border border-amber-200/25 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-100/90"
                          >
                            {String(suspect?.name || suspect?.full_name || `Suspect ${suspectIndex + 1}`)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </aside>
      </div>

    </section>
  );
};

export default ClubPlayPage;
